import { Fragment, useState } from 'react';
import { Link as LinkDeRota } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';

import { formatarValorEmReais } from '../../../shared/dominio/moeda';
import { EstadoDeCarregamento } from '../../../shared/ui/EstadoDeCarregamento';
import { EstadoDeErro } from '../../../shared/ui/EstadoDeErro';
import {
  chaveDoOrcamentoDoIw,
  conferirOrcamentoDoIw,
  obterOrcamentoDoIw,
  ROTULOS_DO_AVISO,
  type ItemDoOrcamentoIwApi,
  type ResultadoDoOrcamentoApi,
} from './apiDoOrcamentoDoIw';
import {
  ROTULOS_DO_ESTADO_DA_LINHA,
  type EstadoDaLinha,
  type ModalidadeDoMotorApi,
  type VeredictoDaLinha,
} from './tipos';
import { DecisaoDoOrcamentoNoIw } from './DecisaoDoOrcamentoNoIw';
import { competenciaDe } from '../../../shared/dominio/datas';

const SEM_ORCAMENTO: Record<Exclude<ResultadoDoOrcamentoApi, 'Encontrado'>, (candidatos: number) => string> = {
  SemCarteira: () =>
    'O Caso não tem carteira (nem o número da SIAI, nem a matrícula do e-mail). Confirme a matrícula na triagem.',
  SemContratoNoIw: () => 'A operadora deste Caso não tem contrato ligado ao IW.',
  NaoEncontrado: () => 'Não há orçamento no IW com a carteira deste Caso nesta competência.',
  Ambiguo: (candidatos) =>
    `Há ${String(candidatos)} orçamentos no IW com a carteira deste Caso nesta competência. Confira no IW qual vale.`,
};

const COR_POR_ESTADO: Record<EstadoDaLinha, 'success' | 'warning' | 'error' | 'default'> = {
  ok: 'success',
  alerta: 'warning',
  barrado: 'error',
  sem_cobertura: 'default',
};

function formatarData(iso: string | null): string {
  if (iso === null) return '—';
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
}

/** O valor da linha: quantidade × preço com desconto (o `valorTotal` do IW só vem preenchido na diária). */
function valorDaLinha(item: ItemDoOrcamentoIwApi): number {
  return item.quantidade * item.valorComDesconto;
}

/** Os grupos na ordem em que aparecem no orçamento, cada um com as suas linhas. */
function porGrupo<T extends { item: ItemDoOrcamentoIwApi }>(pares: readonly T[]): { grupo: string; linhas: T[] }[] {
  const grupos = new Map<string, T[]>();
  for (const par of pares) grupos.set(par.item.grupo, [...(grupos.get(par.item.grupo) ?? []), par]);
  return [...grupos].map(([grupo, linhas]) => ({ grupo, linhas }));
}

interface Par {
  readonly item: ItemDoOrcamentoIwApi;
  readonly veredito: VeredictoDaLinha | null;
}

/** O código com que a linha foi ao motor: o TISS, e o SCC só quando o IW não manda o TISS (`ParaEntradaDoMotor`). */
function chaveDoItem(item: ItemDoOrcamentoIwApi): string | null {
  return item.codigo ?? item.codigoScc ?? null;
}

/** O pacote que inclui a linha, pelo contrato (verificação 4); nulo fora de pacote. */
function pacoteDa(par: Par): string | null {
  return par.veredito?.motivos.find((m) => m.pacote !== null)?.pacote ?? null;
}

/**
 * Os blocos da tabela: cada pacote com as linhas dele e o que o contrato inclui nele; depois os demais itens por grupo
 * do IW; por fim o que o IW deu de graça sem regra do contrato que explique.
 */
function secoes(pares: readonly Par[]): { chave: string; titulo: string; pacote: boolean; linhas: Par[] }[] {
  const codigosDePacote = [...new Set(pares.map(pacoteDa).filter((c): c is string => c !== null))];
  const usados = new Set<Par>();
  const blocos = codigosDePacote.map((codigo) => {
    const linhasDoPacote = pares.filter((p) => chaveDoItem(p.item) === codigo);
    const inclusos = pares.filter((p) => pacoteDa(p) === codigo && !linhasDoPacote.includes(p));
    [...linhasDoPacote, ...inclusos].forEach((p) => usados.add(p));
    const nome = linhasDoPacote[0]?.item.descricao ?? codigo;
    return {
      chave: `pacote:${codigo}`,
      titulo: `Pacote: ${nome}`,
      pacote: true,
      linhas: [...linhasDoPacote, ...inclusos],
    };
  });
  const resto = pares.filter((p) => !usados.has(p));
  const deGraca = resto.filter((p) => p.item.aviso === null && p.item.valorComDesconto === 0);
  const grupos = porGrupo(resto.filter((p) => !deGraca.includes(p))).map((g) => ({
    chave: `grupo:${g.grupo}`,
    titulo: g.grupo,
    pacote: false,
    linhas: g.linhas,
  }));
  return [
    ...blocos,
    ...grupos,
    ...(deGraca.length > 0
      ? [{ chave: 'de-graca', titulo: 'De graça no IW, sem regra no contrato', pacote: false, linhas: deGraca }]
      : []),
  ];
}

/**
 * O rótulo da conferência: dentro de pacote, se o IW cobrou o item (pelo valor com desconto, e não pelo estado da linha,
 * que pode ter alerta de outra verificação); fora, o estado da linha.
 */
function rotuloDaConferencia(par: Par): string | null {
  if (par.veredito === null) return null;
  if (pacoteDa(par) !== null) return par.item.valorComDesconto > 0 ? 'Incluso, mas cobrado' : 'Incluso, sem cobrança';
  return ROTULOS_DO_ESTADO_DA_LINHA[par.veredito.estado];
}

/** O motivo com a regra e a cláusula que o citam (AC9 da `US-037`): "mensagem · R003 · Anexo I p.2". */
function comFonte(motivos: readonly { mensagem: string; fonte: string | null }[]): string {
  return motivos.map((m) => (m.fonte !== null ? `${m.mensagem} · ${m.fonte}` : m.mensagem)).join('; ');
}

function plural(n: number, um: string, varios: string): string {
  return n === 1 ? `1 ${um}` : `${String(n)} ${varios}`;
}

/**
 * Cada item do orçamento com o veredito dele. O motor recebe os itens conferíveis (sem aviso) na ordem do orçamento e
 * devolve o veredito na mesma ordem (`ParaEntradaDoMotor`); item fora da conferência fica sem veredito. `casou` só é
 * verdade quando cada item conferível tem o seu, com o mesmo código: o orçamento pode mudar entre a leitura (cache de 5
 * minutos) e a conferência (que relê), e a mesma contagem em outra ordem poria o veredito na linha errada.
 */
function comVeredito(
  itens: readonly ItemDoOrcamentoIwApi[],
  linhas: readonly VeredictoDaLinha[] | undefined,
): { pares: { item: ItemDoOrcamentoIwApi; veredito: VeredictoDaLinha | null }[]; casou: boolean } {
  const conferiveis = itens.filter((item) => item.aviso === null).length;
  let proxima = 0;
  const pares = itens.map((item) => {
    if (item.aviso !== null || linhas === undefined) return { item, veredito: null };
    return { item, veredito: linhas[proxima++] ?? null };
  });
  const mesmoCodigo = pares.every((p) => p.veredito === null || p.veredito.codigo === chaveDoItem(p.item));
  return { pares, casou: linhas?.length === conferiveis && mesmoCodigo };
}

/**
 * "Orçamento no IW" no Caso (`TP-066`, `TP-071` T12): o que há de errado no orçamento da competência contra o
 * contrato, em cima; os itens, com o veredito de cada um, embaixo e fechados. A conferência roda sozinha ao achar o
 * orçamento: só avisa, e a mesma conferência repetida é a mesma avaliação. Aprovar ou reprovar no IW (`TP-068`) é a
 * única escrita, sempre com a confirmação de uma pessoa.
 */
export function OrcamentoDoIw({
  casoId,
  modalidadeDoCaso,
}: {
  casoId: string;
  modalidadeDoCaso: ModalidadeDoMotorApi | null;
}): React.JSX.Element {
  const [competencia, definirCompetencia] = useState(competenciaDe);
  const [rodada, definirRodada] = useState(0);
  // "Atualizar" vale para a competência em que foi clicado; trocar de mês volta a usar o cache de 5 min.
  const [competenciaAtualizada, definirCompetenciaAtualizada] = useState<string | null>(null);
  const [itensAbertos, definirItensAbertos] = useState(false);

  const consulta = useQuery({
    queryKey: chaveDoOrcamentoDoIw(casoId, competencia, rodada),
    queryFn: ({ signal }) => obterOrcamentoDoIw(casoId, competencia, competenciaAtualizada === competencia, signal),
    enabled: /^\d{4}-\d{2}$/.test(competencia),
    retry: false,
  });

  const dado = consulta.data;
  const orcamento = dado?.resultado === 'Encontrado' ? dado.orcamento : null;

  // Nenhuma das verificações usa a modalidade hoje; vai a do Caso, ou AD sem ela, como antes. Ela entra na chave: com
  // outra modalidade é outra conferência, e não a guardada.
  const modalidade = modalidadeDoCaso ?? 'Ad';
  const conferencia = useQuery({
    queryKey: [
      ...chaveDoOrcamentoDoIw(casoId, competencia, rodada),
      'conferencia',
      orcamento?.budgetId ?? null,
      modalidade,
    ],
    queryFn: () => conferirOrcamentoDoIw(casoId, competencia, modalidade),
    enabled: orcamento !== null,
    retry: false,
    staleTime: Infinity,
  });

  const atualizar = (): void => {
    definirCompetenciaAtualizada(competencia);
    definirRodada((anterior) => anterior + 1);
  };

  const { pares, casou } =
    orcamento !== null
      ? comVeredito(orcamento.itens, conferencia.data?.avaliacao.veredicto.linhas)
      : { pares: [], casou: false };
  const comProblema = pares.filter((p) => p.veredito?.estado === 'alerta' || p.veredito?.estado === 'barrado');
  const semCobertura = pares.filter((p) => p.veredito?.estado === 'sem_cobertura').length;
  // O vínculo de Contrato × IW é pelo SCC: só vale para a linha que o IW manda com SCC (entregável 5 do NR-104).
  const ligaveis = pares.filter((p) => p.veredito?.estado === 'sem_cobertura' && p.item.codigoScc != null).length;
  const fora = pares.filter((p) => p.item.aviso !== null);
  const alertasDeCabecalho = conferencia.data?.avaliacao.veredicto.alertasDeCabecalho ?? [];

  return (
    <Stack spacing={1.5} aria-label="Orçamento no IW" component="section">
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <TextField
          type="month"
          label="Competência"
          size="small"
          value={competencia}
          onChange={(evento) => {
            definirCompetencia(evento.target.value);
          }}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <Button variant="text" startIcon={<RefreshOutlined />} onClick={atualizar} disabled={consulta.isFetching}>
          Atualizar
        </Button>
      </Stack>

      {consulta.isPending && consulta.fetchStatus !== 'idle' ? <EstadoDeCarregamento linhas={3} /> : null}

      {consulta.isError ? (
        <EstadoDeErro
          erro={consulta.error}
          contexto="Não foi possível ler o orçamento do IW"
          aoReprocessar={atualizar}
        />
      ) : null}

      {dado !== undefined && dado.resultado !== 'Encontrado' ? (
        <Alert severity="info">{SEM_ORCAMENTO[dado.resultado](dado.candidatos)}</Alert>
      ) : null}

      {orcamento !== null && dado !== undefined ? (
        <>
          <Typography variant="body2" color="text.secondary">
            {dado.contrato?.nome ?? 'Contrato'} · orçamento {String(orcamento.budgetId)} ·{' '}
            {formatarData(orcamento.inicio)} a {formatarData(orcamento.fim)} · senha{' '}
            {orcamento.codigoAutorizacao ?? 'sem senha'}
            {orcamento.nomePaciente !== null ? ` · paciente no IW: ${orcamento.nomePaciente}` : ''}
          </Typography>

          <DecisaoDoOrcamentoNoIw casoId={casoId} competencia={competencia} orcamento={orcamento.budgetId} />

          {conferencia.isPending ? <EstadoDeCarregamento linhas={2} /> : null}

          {conferencia.isError ? (
            <EstadoDeErro
              erro={conferencia.error}
              contexto="Não foi possível conferir o orçamento com o contrato"
              aoReprocessar={() => {
                void conferencia.refetch();
              }}
            />
          ) : null}

          {conferencia.isSuccess ? (
            <Stack spacing={1} role="status" aria-label="Resultado da conferência">
              {alertasDeCabecalho.map((alerta) => (
                <Alert key={alerta} severity="warning">
                  {alerta}
                </Alert>
              ))}

              {!casou ? (
                <Alert severity="error">
                  A conferência não bate com os itens do orçamento: ele pode ter mudado no IW. Clique em Atualizar e
                  confira de novo.
                </Alert>
              ) : comProblema.length === 0 ? (
                <Alert severity="success">Nenhum item em desacordo com o contrato.</Alert>
              ) : (
                <Alert severity="warning">
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {plural(
                      comProblema.length,
                      'item em desacordo com o contrato',
                      'itens em desacordo com o contrato',
                    )}
                  </Typography>
                  <Stack component="ul" spacing={0.5} sx={{ pl: 2, m: 0 }}>
                    {comProblema.map(({ item, veredito }, indice) => (
                      <Typography component="li" variant="body2" key={`${item.codigo ?? ''}-${String(indice)}`}>
                        <b>{item.descricao}</b> ({item.quantidade.toLocaleString('pt-BR')} ×{' '}
                        {formatarValorEmReais(item.valorUnitario)}):{' '}
                        {veredito !== null ? comFonte(veredito.motivos) : ''}
                      </Typography>
                    ))}
                  </Stack>
                </Alert>
              )}

              {semCobertura > 0 ? (
                <Alert severity="info">
                  {plural(
                    semCobertura,
                    'item ainda não está ligado ao contrato e não foi conferido.',
                    'itens ainda não estão ligados ao contrato e não foram conferidos.',
                  )}{' '}
                  {dado.contrato !== null && ligaveis > 0 ? (
                    <Link component={LinkDeRota} to={`/contratos/${dado.contrato.id}/parametrizacao`}>
                      Ligar em Contrato × IW
                    </Link>
                  ) : ligaveis === 0 ? (
                    'O IW ainda não manda o SCC destes itens, e o vínculo é pelo SCC.'
                  ) : null}
                </Alert>
              ) : null}

              {fora.length > 0 ? (
                <Alert severity="info">
                  {plural(fora.length, 'item ficou de fora', 'itens ficaram de fora')}:{' '}
                  {[...new Set(fora.map((p) => (p.item.aviso !== null ? ROTULOS_DO_AVISO[p.item.aviso] : '')))].join(
                    ', ',
                  )}{' '}
                  no IW.
                </Alert>
              ) : null}
            </Stack>
          ) : null}

          <Button
            variant="text"
            sx={{ alignSelf: 'start' }}
            aria-expanded={itensAbertos}
            onClick={() => {
              definirItensAbertos((aberto) => !aberto);
            }}
          >
            {itensAbertos ? 'Esconder' : 'Ver'} os {String(orcamento.itens.length)} itens do orçamento
          </Button>

          {itensAbertos ? (
            <TableContainer>
              <Table size="small" aria-label="Itens do orçamento no IW">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="right">Qtd</TableCell>
                    <TableCell align="right">Valor unitário</TableCell>
                    <TableCell align="right">Desconto</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Conferência</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {secoes(pares).map(({ chave, titulo, pacote, linhas }) => (
                    <Fragment key={chave}>
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          sx={{ bgcolor: pacote ? 'action.selected' : 'action.hover', fontWeight: 600 }}
                        >
                          {titulo}
                        </TableCell>
                      </TableRow>
                      {linhas.map((par, indice) => {
                        const { item, veredito } = par;
                        const rotulo = rotuloDaConferencia(par);
                        const dentroDoPacote = pacote && indice > 0 && pacoteDa(par) !== null;
                        return (
                          <TableRow key={`${item.codigo ?? 'sem-codigo'}-${String(indice)}`}>
                            <TableCell>{item.codigo ?? item.codigoScc ?? '—'}</TableCell>
                            <TableCell sx={dentroDoPacote ? { pl: 4 } : undefined}>{item.descricao}</TableCell>
                            <TableCell align="right">{item.quantidade.toLocaleString('pt-BR')}</TableCell>
                            <TableCell align="right">{formatarValorEmReais(item.valorUnitario)}</TableCell>
                            <TableCell align="right">{`${item.descontoPercentual.toLocaleString('pt-BR')}%`}</TableCell>
                            <TableCell align="right">{formatarValorEmReais(valorDaLinha(item))}</TableCell>
                            <TableCell>
                              {item.aviso !== null ? (
                                <Typography variant="caption" color="text.secondary">
                                  fora: {ROTULOS_DO_AVISO[item.aviso]}
                                </Typography>
                              ) : veredito !== null && rotulo !== null ? (
                                <Tooltip
                                  // Um motivo por linha, com a regra e a página (AC9 da US-037); sem motivo, nada a mostrar.
                                  title={
                                    veredito.motivos.length > 0 ? (
                                      <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
                                        {veredito.motivos.map((m, i) => (
                                          <li key={`${String(i)}-${m.mensagem}`}>{comFonte([m])}</li>
                                        ))}
                                      </Stack>
                                    ) : (
                                      ''
                                    )
                                  }
                                  placement="left"
                                  describeChild
                                >
                                  <Chip size="small" color={COR_POR_ESTADO[veredito.estado]} label={rotulo} />
                                </Tooltip>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell colSpan={5} align="right" sx={{ color: 'text.secondary' }}>
                          Subtotal {pacote ? 'do pacote' : titulo}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {formatarValorEmReais(linhas.reduce((soma, { item }) => soma + valorDaLinha(item), 0))}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </Fragment>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700 }}>
                      Total dos itens
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {formatarValorEmReais(pares.reduce((soma, { item }) => soma + valorDaLinha(item), 0))}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} align="right">
                      Total do período no IW (o IW só soma a diária)
                    </TableCell>
                    <TableCell align="right">{formatarValorEmReais(orcamento.valorTotalPeriodo)}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}
